'use client'

import clsx from 'clsx'

import ApIcon from '@/assets/icons/tasks-feed/ap.svg'
import ClockIcon from '@/assets/icons/tasks-feed/clock.svg'
import UsersIcon from '@/assets/icons/tasks-feed/users.svg'
import type { TaskCardCta } from '@/features/tasks/taskCardCta'
import type { ActionTaskPlatform } from '@/features/tasks/types/actionTaskDetail'
import { Link } from '@/i18n/routing'
import Image from '@/ui/MediaImage'

import type { TaskCardSocialPlatform } from '../TaskCard/taskCard.constants'
import { formatParticipantCap } from '../TaskCard/taskCard.utils'
import {
  resolveTaskBrandLogoSrc,
  resolveTaskCoverSrc
} from '../TasksTaskCards/tasksTaskCards.constants'
import {
  TASK_FEED_PLATFORM_ICON,
  TASK_FEED_PLATFORM_LABEL,
  TASK_FEED_STORE_ORDER,
  TASK_FEED_TYPE_ICON,
  type TaskFeedPlatform,
  type TaskFeedStatus
} from './taskFeedCard.constants'
import S from './TaskFeedCard.module.scss'

export type TaskFeedCardProps = {
  href: string
  onClick?: () => void
  type: TaskCardCta
  typeLabel: string
  coverSrc?: string | null
  brandLogoSrc?: string
  brandName: string
  title: string
  social?: TaskCardSocialPlatform | null
  /** Every platform of a task group, when it offers more than one. */
  socials?: TaskCardSocialPlatform[]
  actionPlatforms?: ActionTaskPlatform[]
  pointsValue: string
  /** Time left and participants; closed tasks show the reward only. */
  stats?: {
    timeLeftLabel: string
    participantCurrent: number
    participantLimit: number
  }
  status?: TaskFeedStatus
  /** Heading of the «+N» hint listing the platforms left out. */
  alsoOnLabel?: string
}

/** Up to four glyphs; more collapse into three and a «+N» with the rest. */
const PLATFORMS_SHOWN = 4

function TaskFeedCardPlatforms({
  type,
  social,
  socials,
  actionPlatforms = [],
  alsoOnLabel
}: Pick<
  TaskFeedCardProps,
  'type' | 'social' | 'socials' | 'actionPlatforms' | 'alsoOnLabel'
>) {
  const all: TaskFeedPlatform[] =
    type === 'action'
      ? TASK_FEED_STORE_ORDER.filter(store => actionPlatforms.includes(store))
      : socials?.length
      ? socials
      : social
      ? [social]
      : []
  if (all.length === 0) {
    return null
  }
  const shown =
    all.length > PLATFORMS_SHOWN ? all.slice(0, PLATFORMS_SHOWN - 1) : all
  const rest = all.slice(shown.length)

  return (
    <div className={S.platforms}>
      {shown.map(platform => {
        const Icon = TASK_FEED_PLATFORM_ICON[platform]
        return (
          <span className={S.platform} key={platform} aria-hidden>
            <Icon className={S.platformIcon} />
          </span>
        )
      })}
      {rest.length > 0 ? (
        <span className={clsx(S.platform, S.platformMore)}>
          <span aria-hidden>+{rest.length}</span>
          <span className={S.alsoOn} role="tooltip">
            <span className={S.alsoOnTitle}>{alsoOnLabel}</span>
            {rest.map(platform => {
              const Icon = TASK_FEED_PLATFORM_ICON[platform]
              return (
                <span className={S.alsoOnItem} key={platform}>
                  <Icon className={S.platformIcon} aria-hidden />
                  {TASK_FEED_PLATFORM_LABEL[platform]}
                </span>
              )
            })}
          </span>
        </span>
      ) : null}
    </div>
  )
}

export function TaskFeedCard({
  href,
  onClick,
  type,
  typeLabel,
  coverSrc,
  brandLogoSrc,
  brandName,
  title,
  social,
  socials,
  actionPlatforms,
  pointsValue,
  stats,
  status,
  alsoOnLabel
}: TaskFeedCardProps) {
  const TypeIcon = TASK_FEED_TYPE_ICON[type]

  return (
    <Link href={href} className={S.card} onClick={onClick}>
      <div className={S.cover}>
        <Image
          src={resolveTaskCoverSrc(coverSrc)}
          alt=""
          fill
          className={S.coverImg}
          sizes="(max-width: 560px) 100vw, (max-width: 860px) 50vw, (max-width: 1199px) 33vw, 340px"
        />
        <span className={S.typeLabel}>
          <TypeIcon className={S.typeIcon} aria-hidden />
          {typeLabel}
        </span>
        {status ? (
          <span className={clsx(S.status, S[`status_${status.tone}`])}>
            {status.label}
          </span>
        ) : null}
      </div>
      <div className={S.body}>
        <div className={S.brandRow}>
          <div className={S.brand}>
            <span className={S.brandLogo}>
              <Image
                src={resolveTaskBrandLogoSrc(brandLogoSrc)}
                alt=""
                fill
                sizes="24px"
                className={S.brandLogoImg}
              />
            </span>
            <span className={S.brandName}>{brandName}</span>
          </div>
          <TaskFeedCardPlatforms
            type={type}
            social={social}
            socials={socials}
            actionPlatforms={actionPlatforms}
            alsoOnLabel={alsoOnLabel}
          />
        </div>
        <div className={S.content}>
          <h3 className={S.title}>{title}</h3>
          <div className={S.statsRow}>
            {stats ? (
              <div className={S.meta}>
                <span className={S.metaItem}>
                  <ClockIcon className={S.metaIcon} aria-hidden />
                  {stats.timeLeftLabel}
                </span>
                <span className={S.metaDot} aria-hidden />
                <span className={S.metaItem}>
                  <UsersIcon className={S.metaIcon} aria-hidden />
                  {formatParticipantCap(stats.participantCurrent).toLowerCase()}
                  /{formatParticipantCap(stats.participantLimit).toLowerCase()}
                </span>
              </div>
            ) : null}
            <span className={S.reward}>
              <ApIcon className={S.rewardIcon} aria-hidden />
              {pointsValue}
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}
